import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import "../../css/iaat/IAAT.css";
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useNavigate } from "react-router-dom";
import FAQIAATs from "./FAQIAATs";
import IAATExamples from "./IAATExamples";

export default function HomePage() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false); // State for mobile detection
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768); // Adjust 768px to your breakpoint
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Call once to set initial state

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className="homepage-container">
      <Header />
      <section className="iaat-description-section">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="iaat-content"
        >
          {/* Left side: Text */}
          <div className="iaat-text">
            <h2 className="iaat-header">What is the Iowa Algebra Aptitude Test (IAAT)?</h2>
            <p className="iaat-paragraph">
              The Iowa Algebra Aptitude Test (IAAT) helps assess whether students are ready for Algebra I. In some school districts, 6th-grade students take this test to determine their eligibility for Algebra I in 7th grade. It evaluates essential math skills, ensuring students are prepared for the challenges of Algebra—a key subject for future academic success.
            </p>
          </div>

          {/* Right side: Image (visible only if not mobile) */}
          {!isMobile && (
            <div className="iaat-image-container">
              <img src="/iaat.PNG" alt="IAAT Test" className="iaat-image" />
            </div>
          )}
        </motion.div>
      </section>

      {/* Section 2: Four Key Areas of IAAT */}
      <section className="iaat-areas-section">
        <div className="iaat-content">
          {/* Left - Image (visible only if not mobile) */}
          {!isMobile && (
            <div className="iaat-image-container">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="iaat-image-container"
              >
                <img src="/IAAT3.PNG" alt="Four Areas of IAAT" className="iaat-image" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="learn-more-container"
              >
                <span className="learn-more-link" onClick={() => navigate("/IAATExample")}>Learn More About Key Areas</span>
              </motion.div>

            </div>

          )}

          {/* Right - Four Key Areas */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="iaat-text-container"
          >
            <h2 className="iaat-header">Key Areas Assessed in IAAT</h2>
            <p className="iaat-paragraph">
              The IAAT evaluates students across four essential areas, ensuring they have the skills needed for Algebra I.
            </p>

            <div className="iaat-list">
              <motion.div className="iaat-item" whileHover={{ scale: 1.05 }}>
                <span className="iaat-icon">📊</span>
                <div>
                  <strong>Pre-Algebraic Number Skills:</strong> Tests basic math concepts necessary for Algebra success.
                </div>
              </motion.div>

              <motion.div className="iaat-item" whileHover={{ scale: 1.05 }}>
                <span className="iaat-icon">📈</span>
                <div>
                  <strong>Interpreting Mathematical Information:</strong> Measures students' ability to analyze data from graphs or text.
                </div>
              </motion.div>

              <motion.div className="iaat-item" whileHover={{ scale: 1.05 }}>
                <span className="iaat-icon">🔢</span>
                <div>
                  <strong>Representing Relationships:</strong> Evaluates the ability to recognize patterns and express numerical relationships.
                </div>
              </motion.div>

              <motion.div className="iaat-item" whileHover={{ scale: 1.05 }}>
                <span className="iaat-icon">➗</span>
                <div>
                  <strong>Using Symbols:</strong> Assesses understanding of algebraic symbols and their correct usage in equations.
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 3: IAAT Test Details */}
      <section className="iaat-test-details-section">
        <div className="iaat-content">
          {/* Test Overview - visible only if not mobile */}
          {!isMobile && (
            <>
              <motion.h2
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="iaat-header"
              >
                Test Overview
              </motion.h2>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="iaat-paragraph2"
              >
                The Iowa Algebra Aptitude Test (IAAT) efficiently assesses a student's readiness for Algebra I in one class period, offering valuable insights to help educators make informed placement and support decisions.
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="iaat-test-info"
              >
                <p><strong>Test Duration:</strong> 50–60 minutes</p>
                <p><strong>Format:</strong> Typically paper-based</p>
                <p><strong>Scoring:</strong> Centrally processed for accuracy and consistency</p>
              </motion.div>
            </>
          )}
        </div>
      </section>

      <IAATExamples />
      <FAQIAATs />
      {/* Footer */}
      <Footer />
    </div>
  );
}
